import logger from '../../utils/logger';
import { toNumber } from '../../utils/numbers';
import convertUnit from '../../utils/unit-converter';
import {
  ConstituentType,
  WeighedMaterialType,
} from '../../constants/MaterialModels';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { timerKey } from '../../utils/timer-manager/timer-manager';

const findPumpData = (pumps, itemId) => {
  if (!pumps) {
    return null;
  }
  return Object.values(pumps).find(p => p.material.itemId === itemId);
};

/**
 * Get water to add to wash water for live moisture
 * @param batchStore
 * @param {WeighedDataToBatch} data
 * @param {BatchData} runningData
 * @param {number} aggTarget
 */
export const getWaterToAdjust = (batchStore, {
  runningData,
  data,
  aggTarget,
}) => {
  const { waterUnit, dropSize } = runningData;
  const { constituent, scale, material, materialDetails } = data;
  const scaleUnit = batchStore.getUnitById(scale.unitId);
  const moistureDeduction = constituent.moistureDeduction || 0;
  const waters = runningData.mixDesign.filter(v => v.type === ConstituentType.WATER && v.quantityNet > 0);
  let waterToAdjust = runningData.waterToAdjust || 0;
  let moistureDiff = toNumber(
    convertUnit(
      constituent.quantityNet * ((materialDetails.autoMoistureAmount - materialDetails.absorption) / 100),
      scaleUnit,
      waterUnit,
    ), 2);

  waters.forEach((w) => {
    let waterData = findPumpData(runningData.pumps, w.constituentItemId);
    if (!waterData) {
      Object.values(runningData.scales).forEach(scaleData => {
        waterData = scaleData.find(v => v.material.itemId === w.constituentItemId);
      });
    }

    if (waterData) {
      const { itemBatchUnitSymbol } = waterData.materialDetails;

      if (itemBatchUnitSymbol !== waterUnit) {
        moistureDiff = convertUnit(
          moistureDiff,
          waterUnit,
          itemBatchUnitSymbol
        );
      }

      const waterToAdd = ((moistureDeduction - moistureDiff) / waters.length) * dropSize;
      waterToAdjust += convertUnit(
        waterToAdd,
        itemBatchUnitSymbol,
        waterUnit,
      );

      logger.log('live moisture, waterTarget', waterData.material.code, waterToAdd,
        'autoMoistureAmount:', materialDetails.autoMoistureAmount,
        moistureDeduction, moistureDiff, waterToAdjust, waterToAdd,
        moistureDiff, w.quantityNet, itemBatchUnitSymbol, waters.length, runningData);
    }
  });

  logger.log('live moisture, adjust target', material.code, 'new target:', aggTarget,
    'live moisture:', materialDetails.autoMoistureAmount, '%', 'moistureDiff', moistureDiff);

  return toNumber(waterToAdjust);
};

const startWaterBin = (batchStore, runningData) => {
  if (!runningData?.scales) {
    return;
  }

  Object.values(runningData.scales).forEach(scaleData => {
    let waterData = scaleData.find(v => v.materialDetails.type === WeighedMaterialType.Water
      || (v.materialDetails.type === WeighedMaterialType.SecondWater && toNumber(runningData.secondWater) === 100)
    );

    if (waterData?.bin?.id) {
      const binId = waterData.bin.id;
      let binStatus = batchStore.getDeviceStatus(binId);

      if (binStatus.uid === runningData.uid && binStatus.currentDrop === runningData.currentDrop
        && [DeviceStatus.WAIT_MOIST].includes(binStatus.status)) {
        logger.log('live moisture, no water to adjust, start bin', waterData.bin.description, binStatus.status);
        batchStore.timers.clear(timerKey.device(binId));
        batchStore.timers.set(timerKey.device(binId), () => {
          binStatus = batchStore.getDeviceStatus(binId);
          if ([DeviceStatus.WAIT_MOIST].includes(binStatus.status)) {
            batchStore.feedMode(waterData, runningData, waterData.mft + binStatus.prevWeight);
          }
        }, 2000, `Start water bin with delay - bin #${binId}`);
      }
    }
  });
};

export const updateWeighedWaterTarget = (batchStore, runningData, data, aggTarget) => {
  const { headWater, washWater, waterUnit, currentDrop, uid } = runningData;
  const waterToAdjust = getWaterToAdjust(batchStore, { data, runningData, aggTarget });

  if (!waterToAdjust) {
    logger.log('live moisture, no water to adjust', waterToAdjust);
    startWaterBin(batchStore, runningData);

    return;
  }

  if (!headWater && !washWater) {
    // reduce water in the next drop
    logger.log('live moisture: there is neither a head nor wash water in the current drop, water will be adjusted in the next drop', waterToAdjust);

    let nextDrop = batchStore.running.find(v => v.uid === uid && v.currentDrop > currentDrop);
    if (!nextDrop) {
      nextDrop = batchStore.queue.find(v => v.uid === uid && v.currentDrop > currentDrop);
    }

    if (nextDrop) {
      nextDrop.waterToAdjust += waterToAdjust;
    } else if (waterToAdjust > 0) {
      logger.warn('can\'t adjust water, no next drop', waterToAdjust);
      batchStore.showWarning(`Live moisture, can't adjust water. There is neither a head nor wash water.
       Please add ${waterToAdjust} ${waterUnit} manually!`, 10000);
    }

    return;
  }

  if (headWater) {
    runningData.headWater += waterToAdjust;
  } else if (washWater) {
    runningData.washWater += waterToAdjust;
  }

  runningData.waterToAdjust = 0;

  Object.values(runningData.scales).forEach(scaleData => {
    let waterData = scaleData.find(v => v.materialDetails.type === WeighedMaterialType.Water
      || (v.materialDetails.type === WeighedMaterialType.SecondWater && toNumber(runningData.secondWater) === 100)
    );
    if (waterData) {
      const scaleUnit = batchStore.getUnitById(waterData.scale.unitId);
      const convertedHeadWater = runningData.headWater
        ? convertUnit(
          runningData.headWater,
          waterUnit,
          scaleUnit,
        )
        : 0;
      const convertedWashWater = runningData.washWater
        ? convertUnit(
          runningData.washWater,
          waterUnit,
          scaleUnit,
        )
        : 0;
      const convertedWaterToAdjust = waterToAdjust
        ? convertUnit(
          waterToAdjust,
          waterUnit,
          scaleUnit,
        )
        : 0;

      waterData.mft += toNumber(convertedWaterToAdjust);
      waterData.unconvertedTarget += toNumber(waterToAdjust);

      logger.log(`live moisture, adjust ${waterData.material.code},
        head water: ${runningData.headWater} => ${convertedHeadWater} ${scaleUnit},
        wash water: ${runningData.washWater} => ${convertedWashWater} ${scaleUnit},
        all water: ${(runningData.headWater + runningData.washWater)} => ${(convertedHeadWater + convertedWashWater)} ${scaleUnit},
        new target: ${waterData.unconvertedTarget} => ${waterData.mft} ${scaleUnit},
        second water: ${runningData.secondWater} %,
        added: ${waterToAdjust} ${waterUnit} => ${convertedWaterToAdjust} ${scaleUnit}`);

      const binStatus = batchStore.getDeviceStatus(waterData.bin?.id);

      if (binStatus.uid === runningData.uid && binStatus.currentDrop === runningData.currentDrop) {
        if (binStatus.status === DeviceStatus.FEED) {
          const mftPrimary = waterData.mft - batchStore.getFreeFall(waterData.bin, runningData);

          logger.log('live moisture, send new water target when FEED-ing', mftPrimary, waterData.mft, scaleUnit);

          batchStore.setWeight('+', waterData.scale?.inputPNo, 0, mftPrimary, 0, waterData.bin?.gateOnePNo);
        }

        batchStore.deviceStatus.set(waterData.bin?.id, {
          ...binStatus,
          target: waterData.mft,
        });

        batchStore.deviceStatus.set(waterData.scale?.id, {
          ...batchStore.getDeviceStatus(waterData.scale?.id),
          target: waterData.mft,
        });

        if ([DeviceStatus.WAIT_MOIST, DeviceStatus.DELAY].includes(binStatus.status)) {
          batchStore.timers.clear(timerKey.device(waterData.bin?.id));
          batchStore.feedMode(waterData, runningData, waterData.mft + binStatus.prevWeight);
        }
      }
    }
  });
};

/**
 * Update water target after live moisture
 *
 * @param batchStore
 * @param {BatchData} runningData
 * @param {WeighedDataToBatch} data
 * @param {number} aggTarget
 */
export const updateWaterTarget = (batchStore, {
  runningData,
  data,
  aggTarget,
}) => {
  if (runningData.isWeighedWaterInTheMix) {
    return updateWeighedWaterTarget(batchStore, runningData, data, aggTarget);
  }

  const { headWater, washWater, waterUnit, currentDrop, allDrops, uid } = runningData;
  const waterToAdjust = getWaterToAdjust(batchStore, { data, runningData, aggTarget });

  if (!waterToAdjust) {
    logger.log('live moisture, no water to adjust', waterToAdjust);
    return;
  }

  if (headWater) {
    runningData.headWater += waterToAdjust;
    runningData.waterToAdjust = 0;
    const waterPumps = Object.values(runningData.pumps).filter(v => v.headWater);
    const allTargets = waterPumps.map(v => v.mft).reduce((a, b) => a + b, 0);

    Object.values(runningData.pumps).forEach(v => {
      if (v.headWater) {
        const percent = v.mft / allTargets;
        const convertedHeadWater = convertUnit(
          runningData.headWater * percent,
          waterUnit,
          v.materialDetails.itemBatchUnitSymbol,
        );
        const added = convertedHeadWater - v.headWater;
        v.headWater = convertedHeadWater;
        v.mft = toNumber(v.headWater + v.washWater);

        logger.log(`live moisture, adjust ${v.material.code},
          head water: ${v.headWater}, new target: ${v.mft}, added: ${added} ${v.materialDetails.itemBatchUnitSymbol}
           total added: ${waterToAdjust} ${waterUnit}`, 'wash water:', v.washWater);

        const ps = batchStore.getDeviceStatus(v.pump.id);
        if (ps.uid === uid && ps.currentDrop === currentDrop) {
          if (ps.status === DeviceStatus.FEED) {
            //send new target for head water during the pump feed
            const meterRate = v.materialDetails.meterRate || 1;
            const target = v.initialWaterValue + toNumber((v.headWater - v.materialDetails.cutOff) / meterRate) || 1;
            logger.log('live moisture, send new target for head water:', v.headWater, meterRate, target, v.initialWaterValue);
            batchStore.setMeter(v.pump.meterPNo, target, v.pump.gatePNo);
          }
          batchStore.deviceStatus.set(v.pump.id, {
            ...ps,
            target: v.mft,
          });
        }
      }
    });
  } else if (currentDrop === allDrops && !headWater && washWater) {
    //no head water, adjust wash water
    runningData.washWater += waterToAdjust;
    runningData.waterToAdjust = 0;
    const waterPumps = Object.values(runningData.pumps).filter(v => v.washWater);
    const allTargets = waterPumps.map(v => v.mft).reduce((a, b) => a + b, 0);

    Object.values(runningData.pumps).forEach(v => {
      if (v.washWater) {
        const percent = v.mft / allTargets;
        const convertedWashWater = convertUnit(
          runningData.washWater * percent,
          waterUnit,
          v.materialDetails.itemBatchUnitSymbol,
        );
        const added = convertedWashWater - v.washWater;
        v.washWater = convertedWashWater;
        v.mft = toNumber(v.headWater + v.washWater);

        logger.log(`live moisture, adjust ${v.material.code},
          wash water: ${v.washWater}, new target: ${v.mft}, added: ${added} ${v.materialDetails.itemBatchUnitSymbol}
           total added: ${waterToAdjust} ${waterUnit}`, runningData.washWater);

        const ps = batchStore.getDeviceStatus(v.pump.id);
        if (ps.uid === uid && ps.currentDrop === currentDrop) {
          if (ps.status === DeviceStatus.FEED) {
            //send new target for water during the pump feed
            const meterRate = v.materialDetails.meterRate || 1;
            const target = v.initialWaterValue + toNumber((v.mft - v.materialDetails.cutOff) / meterRate) || 1;
            logger.log('live moisture, send new target for wash water:', v.mft, v.washWater, meterRate, target, v.initialWaterValue);
            batchStore.setMeter(v.pump.meterPNo, target, v.pump.gatePNo);
          }
          batchStore.deviceStatus.set(v.pump.id, {
            ...ps,
            target: v.mft,
          });
        }
      }
    });
  } else {
    // reduce water in the next drop
    logger.log('live moisture: there is neither a head nor wash water in the current drop, water will be adjusted in the next drop', waterToAdjust);

    let nextDrop = batchStore.running.find(v => v.uid === uid && v.currentDrop > currentDrop);
    if (!nextDrop) {
      nextDrop = batchStore.queue.find(v => v.uid === uid && v.currentDrop > currentDrop);
    }
    if (nextDrop) {
      nextDrop.waterToAdjust += waterToAdjust;
    } else if (waterToAdjust > 0) {
      logger.warn('can\'t adjust water, no next drop', waterToAdjust);
      batchStore.showWarning(`Live moisture, can't adjust water. There is neither a head nor wash water.
         Please add ${waterToAdjust} ${waterUnit} manually!`, 10000);
    }
  }

};

/**
 * Check target from previous drops
 *
 * @param {BatchData} runningData
 * @param {number} itemId
 * @returns {number}
 */
export const getTargetFromPreviousDrops = (runningData, itemId) => {
  let target = 0;
  Object.keys(runningData.batchDrops).forEach(dropNumber => {
    if (Number(dropNumber) < runningData.currentDrop) {
      runningData.batchDrops[dropNumber].forEach(item => {
        if (item.constituentItemId === itemId) {
          target += item.targetPerDrop;
        }
      });
    }
  });

  return target;
};

export const getAverageMoisture = (runningData, itemId, unit = 'lb') => {
  if (!Object.keys(runningData.batchDrops).length) {
    return 0;
  }

  let totalTargets = 0;
  let totalWater = 0;
  let itemCode = '';

  Object.keys(runningData.batchDrops).forEach(dropNumber => {
    runningData.batchDrops[dropNumber].forEach((item) => {
      if (item.constituentItemId === itemId) {
        itemCode = item.batchCode || item.constituentItemCode;
        const isDifferentUnit = item.unitSymbol !== unit;
        const dropTarget = isDifferentUnit ? convertUnit(item.targetPerDrop, item.unitSymbol, unit) : item.targetPerDrop;
        const dropWater = dropTarget - (dropTarget / ( 1 + item.moisture/100));
        totalTargets += dropTarget - dropWater;
        totalWater += dropWater;
        logger.log('moisture per drop:', dropNumber, itemCode, item.moisture, item.targetPerDrop);
      }
    });
  });

  const average = toNumber((totalWater/totalTargets) * 100, 2);
  logger.log(`average moisture ${itemCode} from all drops: ${Object.keys(runningData.batchDrops).length}`,
    average, 'total targets', totalTargets, 'total water', totalWater);

  return average;
};

/**
 * Update mix design after live moisture
 *
 * @param {BatchData} runningData
 * @param {WeighedDataToBatch} data
 * @param {number} aggTarget
 * @param batchStore
 */
export const updateMixDesign = (runningData, data, aggTarget, batchStore) => {
  const { currentDrop, allDrops } = runningData;
  const { material, materialDetails } = data;
  const waters = runningData.mixDesign.filter(v => v.type === ConstituentType.WATER && v.quantityNet > 0);
  let weighedWaterData;

  if (currentDrop !== allDrops) {
    return;
  }

  runningData.mixDesign.forEach(v => {
    if (v.constituentItemId === material.itemId) {
      const aggPrevTargets = getTargetFromPreviousDrops(runningData, material.itemId);
      v.quantityBatchSize = toNumber(aggTarget + aggPrevTargets);
      const tolerance = batchStore.getTolerance(materialDetails, v.quantityBatchSize);
      v.high = toNumber(v.quantityBatchSize + tolerance?.over);
      v.low = toNumber(v.quantityBatchSize - tolerance?.under);
      logger.log('live moisture, adjusting ticket target', material.code, v.quantityBatchSize, aggPrevTargets, aggTarget, v.moisture, v.low, v.high);
    }

    if (runningData.isWeighedWaterInTheMix) {
      Object.values(runningData.scales).forEach(scaleData => {
        weighedWaterData = scaleData.find(v => v.materialDetails.type === WeighedMaterialType.Water
          || (v.materialDetails.type === WeighedMaterialType.SecondWater && toNumber(runningData.secondWater) === 100)
        );
      });
    }

    waters.forEach(w => {
      if (v.constituentItemId === w.constituentItemId) {
        let pumpData = findPumpData(runningData.pumps, w.constituentItemId);
        const targetFromPreviousDrops = getTargetFromPreviousDrops(runningData, v.constituentItemId);

        if (pumpData) {
          v.quantityBatchSize = toNumber(pumpData.mft + targetFromPreviousDrops, 2);
        } else if (weighedWaterData) {
          const scaleUnit = batchStore.getUnitById(weighedWaterData.scale?.unitId);
          const materialUnit = weighedWaterData.materialDetails?.itemBatchUnitSymbol;

          if (scaleUnit === v.unitSymbol) {
            v.quantityBatchSize = toNumber(targetFromPreviousDrops + weighedWaterData.mft);
          } else {
            v.quantityBatchSize = toNumber(targetFromPreviousDrops + convertUnit(weighedWaterData.mft, scaleUnit, materialUnit));
          }
        }

        logger.log('live moisture, adjusting water ticket target:', v.quantityBatchSize, targetFromPreviousDrops);
      }
    });
  });
};

/**
 * Update aggregate target after live moisture
 *
 * @param batchStore
 * @param {BatchData} runningData
 * @param {WeighedDataToBatch|DataToBatch} data
 * @param {number} mft
 * @param {number} aggTarget
 */
export const updateAggTarget = (batchStore, {
  runningData,
  data,
  mft,
  aggTarget,
}) => {
  const { scale, material, materialDetails } = data;
  const batchUnit = materialDetails.itemBatchUnitSymbol;
  const scaleUnit = batchStore.getUnitById(scale.unitId);
  const isDifferentUnit  = batchUnit ? batchUnit !== scaleUnit : false;
  const convertedTarget = isDifferentUnit  ? toNumber(convertUnit(aggTarget, batchUnit, scaleUnit), 5) : aggTarget;
  const diffTarget = convertedTarget - data.mft;

  data.unconvertedTarget = aggTarget;
  data.mft = convertedTarget;

  const scaleIndex = runningData.scales[scale.id].findIndex(v => v.material.itemId === material.itemId);
  runningData.scales[scale.id][scaleIndex] = data;
  mft += diffTarget;

  batchStore.deviceStatus.set(scale.id, {
    ...batchStore.getDeviceStatusDefault(scale),
    target: mft,
  });

  return mft;
};

export const waterShouldWaitForMoistureProbe = (runningData, batchStore) => {
  let aggWithLiveMoistureStillFeeding = 0;

  if (!runningData?.scales) {
    return false;
  }

  Object.keys(runningData?.scales).forEach(scaleId => {
    runningData.scales[scaleId].forEach(item => {
      if (item.materialDetails?.liveMoisture && !item.isFeedFinish) {
        const deviceStatus = batchStore.getDeviceStatus(item.bin?.id);

        if ([DeviceStatus.FEED, DeviceStatus.DELAY, DeviceStatus.IDLE].includes(deviceStatus.status)) {
          aggWithLiveMoistureStillFeeding += 1;
        }
      }
    });
  });

  logger.log('aggWithLiveMoistureStillFeeding', aggWithLiveMoistureStillFeeding);
  return aggWithLiveMoistureStillFeeding > 0;
};

export const getAutoMoistureWithinChangeLimit = (autoMoistureAmount, materialDetails) => {
  let newAutoMoistureAmount = materialDetails.autoMoistureAmount;
  const limit = materialDetails.changeLimitForMoisture || 1;

  if (
    autoMoistureAmount <= (materialDetails.autoMoistureMaxAmount || 15)
    && autoMoistureAmount >= materialDetails.autoMoistureMinAmount
    && autoMoistureAmount !== materialDetails.autoMoistureAmount
  ) {
    if (autoMoistureAmount > materialDetails.autoMoistureAmount + limit) {
      newAutoMoistureAmount += limit;
    } else if (autoMoistureAmount < materialDetails.autoMoistureAmount - limit) {
      newAutoMoistureAmount -= limit;
    } else {
      newAutoMoistureAmount = autoMoistureAmount;
    }
    if (newAutoMoistureAmount < materialDetails.autoMoistureMinAmount) {
      newAutoMoistureAmount = materialDetails.autoMoistureMinAmount;
    }
  }

  return newAutoMoistureAmount;
};
